// src/components/CardShop.tsx - Enhanced with Image Caching
import React, { useState, useEffect } from 'react';
import { ddApi } from '../lib/supabaseClient';
import { Card } from '../types';
import CardDisplay from './CardDisplay';
import { useAuth } from '../context/AuthContext';
import { preloadImages, getCardImageUrls } from '../utils/imageCache';

interface CardShopProps {
  onPurchaseComplete?: () => void;
}

type FilterType = 'all' | 'affordable' | 'owned' | 'unowned';
type RarityFilter = 'all' | 'common' | 'rare' | 'legendary';

const CardShop: React.FC<CardShopProps> = ({ onPurchaseComplete }) => {
  const { user, profile, refreshProfile } = useAuth();
  const [allCards, setAllCards] = useState<Card[]>([]);
  const [playerCards, setPlayerCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<number | null>(null);
  const [filter, setFilter] = useState<FilterType>('unowned');
  const [rarityFilter, setRarityFilter] = useState<RarityFilter>('all');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    loadShopData();
  }, [user]);

  const loadShopData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const [shopCards, ownedCards] = await Promise.all([
        ddApi.getShopCards(),
        ddApi.getPlayerCards(user.id)
      ]);

      setAllCards(shopCards);
      setPlayerCards(ownedCards.map(pc => pc.card!));

      // SIMPLE CACHING: Preload shop card images in background
      const shopImageUrls = getCardImageUrls(shopCards);
      preloadImages(shopImageUrls).catch(() => {
        // Silently handle preload failures - main functionality still works
      });

    } catch (error) {
      console.error('Error loading shop:', error);
      setError('Failed to load shop');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (card: Card) => {
    if (!user || purchasing) return;
    
    // Client-side validation for UX
    if (!profile || profile.cc_balance < card.price) {
      setError(`Insufficient CC. Need ${card.price}, have ${profile?.cc_balance || 0}`);
      return;
    }

    if (isOwned(card.id)) {
      setError('You already own this card');
      return;
    }

    try {
      setPurchasing(card.id);
      setError(null);
      setSuccess(null);

      // SECURE: Server validates everything
      const result = await ddApi.purchaseCard(user.id, card.id);

      if (!result.success) {
        setError(result.error || 'Purchase failed');
        return;
      }

      // Success!
      setSuccess(`Successfully purchased ${card.name}!`);
      
      // Refresh data
      await Promise.all([
        loadShopData(),
        refreshProfile()
      ]);

      if (onPurchaseComplete) {
        onPurchaseComplete();
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);

    } catch (error) {
      console.error('Purchase error:', error);
      setError('Network error - please try again');
    } finally {
      setPurchasing(null);
    }
  };

  const isOwned = (cardId: number): boolean => {
    return playerCards.some(card => card.id === cardId);
  };

  const canAfford = (price: number): boolean => {
    return (profile?.cc_balance || 0) >= price;
  };

  const getFilteredCards = (): Card[] => {
    let filtered = allCards;

    // Apply ownership filter
    switch (filter) {
      case 'owned':
        filtered = filtered.filter(card => isOwned(card.id));
        break;
      case 'unowned':
        filtered = filtered.filter(card => !isOwned(card.id));
        break;
      case 'affordable':
        filtered = filtered.filter(card => 
          !isOwned(card.id) && canAfford(card.price)
        );
        break;
    }

    // Apply rarity filter
    if (rarityFilter !== 'all') {
      filtered = filtered.filter(card => card.rarity === rarityFilter);
    }

    return filtered;
  };

  const displayCards = getFilteredCards();
  const ownedCount = playerCards.length;
  const affordableCount = allCards.filter(card => 
    !isOwned(card.id) && canAfford(card.price)
  ).length;

  if (!user || !profile) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Please log in to access the shop</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Shop Header */}
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-blue-400">🛒 Card Shop</h2>
          <div className="text-right">
            <p className="text-2xl font-bold text-yellow-400">
              {profile.cc_balance} CC
            </p>
            <p className="text-sm text-gray-400">Your Balance</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="bg-gray-700 rounded p-3">
            <div className="text-xl font-bold text-white">{allCards.length}</div>
            <div className="text-xs text-gray-400">Total Cards</div>
          </div>
          <div className="bg-gray-700 rounded p-3">
            <div className="text-xl font-bold text-green-400">{ownedCount}</div>
            <div className="text-xs text-gray-400">Owned</div>
          </div>
          <div className="bg-gray-700 rounded p-3">
            <div className="text-xl font-bold text-blue-400">{affordableCount}</div>
            <div className="text-xs text-gray-400">Affordable</div>
          </div>
          <div className="bg-gray-700 rounded p-3">
            <div className="text-xl font-bold text-purple-400">
              {Math.floor((ownedCount / allCards.length) * 100)}%
            </div>
            <div className="text-xs text-gray-400">Collection</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-lg p-4">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Ownership Filter */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as FilterType)}
            className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
          >
            <option value="all">All Cards ({allCards.length})</option>
            <option value="unowned">Available to Buy ({allCards.length - ownedCount})</option>
            <option value="affordable">Can Afford ({affordableCount})</option>
            <option value="owned">Already Owned ({ownedCount})</option>
          </select>

          {/* Rarity Filter */}
          <select
            value={rarityFilter}
            onChange={(e) => setRarityFilter(e.target.value as RarityFilter)}
            className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
          >
            <option value="all">All Rarities</option>
            <option value="legendary">💎 Legendary (300 CC)</option>
            <option value="rare">🔹 Rare (200 CC)</option>
            <option value="common">⚪ Common (100 CC)</option>
          </select>

          {/* Clear Filters */}
          <button
            onClick={() => {
              setFilter('unowned');
              setRarityFilter('all');
            }}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-600 text-white p-4 rounded-lg">
          <p>{error}</p>
          <button 
            onClick={() => setError(null)}
            className="mt-2 bg-red-700 hover:bg-red-800 px-3 py-1 rounded text-sm"
          >
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-600 text-white p-4 rounded-lg">
          <p>🎉 {success}</p>
        </div>
      )}

      {/* Shop Results */}
      <div className="mb-4">
        <p className="text-gray-400">
          Showing {displayCards.length} cards
        </p>
      </div>

      {/* Cards Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-400">Loading shop...</div>
        </div>
      ) : displayCards.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          </div>
          <h3 className="text-xl font-medium text-white mb-2">No cards found</h3>
          <p className="text-gray-400">Try adjusting your filters</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {displayCards.map(card => {
            const owned = isOwned(card.id);
            const affordable = canAfford(card.price);
            const canPurchase = !owned && affordable && !purchasing;

            return (
              <div key={card.id} className="relative">
                <CardDisplay 
                  card={card} 
                  showDetails 
                  disabled={!canPurchase}
                />
                
                {/* Price Tag */}
                <div className="absolute top-2 left-2">
                  <div className={`px-2 py-1 rounded text-xs font-bold ${
                    card.rarity === 'legendary' ? 'bg-yellow-600 text-black' :
                    card.rarity === 'rare' ? 'bg-blue-600 text-white' :
                    'bg-gray-600 text-white'
                  }`}>
                    {card.price} CC
                  </div>
                </div>

                {/* Status Overlay */}
                {owned && (
                  <div className="absolute inset-0 bg-green-900 bg-opacity-80 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-green-400 text-2xl mb-2">✅</div>
                      <div className="text-white font-medium">Owned</div>
                    </div>
                  </div>
                )}

                {!owned && !affordable && (
                  <div className="absolute inset-0 bg-red-900 bg-opacity-80 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-red-400 text-2xl mb-2">💰</div>
                      <div className="text-white font-medium">Need {card.price - (profile?.cc_balance || 0)} more CC</div>
                    </div>
                  </div>
                )}

                {/* Purchase Button */}
                {!owned && (
                  <div className="mt-3">
                    <button
                      onClick={() => handlePurchase(card)}
                      disabled={!affordable || purchasing === card.id}
                      className={`w-full px-4 py-2 rounded font-medium transition-colors ${
                        affordable
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {purchasing === card.id ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Purchasing...
                        </span>
                      ) : affordable ? (
                        `Buy for ${card.price} CC`
                      ) : (
                        `Need ${card.price} CC`
                      )}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CardShop;